import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

interface TokenRefreshResponse {
  success: boolean;
  token?: string;
  message?: string;
}

class TokenManager {
  private static instance: TokenManager;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value: string) => void;
    reject: (error: any) => void;
  }> = [];

  constructor() {
    this.setupInterceptors();
  }

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  private setupInterceptors() {
    // Request interceptor
    axios.interceptors.request.use(
      (config) => {
        // Don't add token to login/register requests to avoid interference
        const isAuthRequest = config.url?.includes('/api/login') || 
                             config.url?.includes('/api/register') ||
                             config.url?.includes('/api/system-admin/login');
        
        if (!isAuthRequest) {
          const token = localStorage.getItem('token');
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    axios.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        const originalRequest = error.config;

        // Check if error is due to expired token
        if (error.response?.status === 403 || 
            (error.response?.status === 401 && 
             (error.response?.data?.message?.includes('expired') ||
              error.response?.data?.message?.includes('inválido') ||
              error.response?.data?.message?.includes('Token')))) {
          
          if (!this.isRefreshing) {
            this.isRefreshing = true;

            try {
              const newToken = await this.refreshToken();
              this.isRefreshing = false;
              
              // Process all failed requests with new token
              this.failedQueue.forEach(({ resolve }) => resolve(newToken));
              this.failedQueue = [];

              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axios(originalRequest);

            } catch (refreshError) {
              this.isRefreshing = false;
              
              // Clear failed queue and redirect to login
              this.failedQueue.forEach(({ reject }) => reject(refreshError));
              this.failedQueue = [];
              
              this.handleTokenRefreshFailure();
              return Promise.reject(refreshError);
            }
          }

          // If token is being refreshed, queue this request
          return new Promise((resolve, reject) => {
            this.failedQueue.push({
              resolve: (token: string) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(axios(originalRequest));
              },
              reject
            });
          });
        }

        return Promise.reject(error);
      }
    );
  }

  private async refreshToken(): Promise<string> {
    try {
      const currentToken = localStorage.getItem('token');
      if (!currentToken) {
        throw new Error('No token available for refresh');
      }

      // Try to get a new token using the current token
      const response = await axios.post<TokenRefreshResponse>(
        '/api/refresh-token',
        {},
        {
          headers: {
            'Authorization': `Bearer ${currentToken}`
          }
        }
      );

      if (response.data.success && response.data.token) {
        localStorage.setItem('token', response.data.token);
        
        // Also try alternative storage keys for compatibility
        localStorage.setItem('authToken', response.data.token);
        
        console.log('✅ Token refreshed successfully');
        return response.data.token;
      } else {
        throw new Error(response.data.message || 'Failed to refresh token');
      }
    } catch (error: any) {
      console.error('❌ Token refresh failed:', error.message);
      throw error;
    }
  }

  private handleTokenRefreshFailure() {
    // Clear all stored tokens and user data
    const keysToRemove = [
      'token', 
      'authToken', 
      'username', 
      'userRole', 
      'allowedCourses', 
      'allowedDimensions',
      'lastLogin'
    ];
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Show user-friendly notification
    const notification = document.createElement('div');
    notification.className = 'fixed top-5 right-5 bg-red-100 text-red-800 p-4 rounded-md shadow-lg z-50';
    notification.innerHTML = `
      <div class="flex items-center">
        <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
        </svg>
        <span>Sesión expirada. Por favor, inicie sesión nuevamente.</span>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Auto remove notification after 5 seconds
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 5000);

    // Redirect to login page after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  }

  // Public method to manually refresh token
  public async manualRefresh(): Promise<boolean> {
    try {
      await this.refreshToken();
      return true;
    } catch (error) {
      this.handleTokenRefreshFailure();
      return false;
    }
  }

  // Check if token is expired (basic check)
  public isTokenExpired(): boolean {
    const token = localStorage.getItem('token');
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      // Check if token expires in the next 60 seconds
      return payload.exp && payload.exp < (currentTime + 60);
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  }

  // Proactively refresh token if it's about to expire
  public async proactiveRefresh(): Promise<void> {
    if (this.isTokenExpired() && !this.isRefreshing) {
      try {
        await this.refreshToken();
        console.log('✅ Token proactively refreshed');
      } catch (error) {
        console.error('❌ Proactive token refresh failed:', error);
      }
    }
  }

  // Public method to clear session when token is expired
  public clearExpiredSession(): void {
    console.log('🧹 Clearing expired session...');
    this.handleTokenRefreshFailure();
  }

  // Public method to check if we should clear the session
  public shouldClearSession(): boolean {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      const payload = JSON.parse(jsonPayload);
      const currentTime = Math.floor(Date.now() / 1000);
      
      // If token is expired, mark for clearing
      return payload.exp < currentTime;
    } catch (error) {
      // If token is malformed, also clear it
      console.error('❌ Error parsing token:', error);
      return true;
    }
  }
}

// Export singleton instance
export const tokenManager = TokenManager.getInstance();
export default TokenManager;
